import type { FC } from "react";
import { useEffect, useRef, useState } from "react";

import { debounce, uniq } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import type { ISite, IDraggableItem } from "@similarweb/ui-components";
import { findParentByClass } from "@similarweb/ui-components";

import { TOGGLE_OFF_COMPARE_DROPDOWN } from "app/action_types/website_analysis_action-types";
import { AutocompleteWebsitePagesCompareItem } from "pro-features/components/AutocompleteWebsites/AutocompleteWebsitePageCompareItem";
import type { IWebsitePageResult, WebsitePageSearchType } from "pro-features/components/AutocompleteWebsites/types";
import {
    getDomainWithSubdomain,
    getUrlComponents,
} from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { transformSiteListToParam } from "pro-features/components/AutocompleteWebsites/utils";
import { CHART_COLORS } from "app/constants/ChartColors";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { SwTrack } from "app/services/SwTrack";
import { useWebsitePageQueryBarData } from "./useWebsitePageQueryBarData";
import { getButtonToDisplay, getCompareList, getMainItem, FavoriteButton } from "./websitePageAutocompleteItems";
import type { IChosenItem, IChosenItemParams } from "./WebsitePageQueryBarUtils";
import { changeQueryBarOrder, createChosenItem, transformSite } from "./WebsitePageQueryBarUtils";
import type { ISimilarSite } from "./WebsiteQueryBar";
import {
    LoadingComponent,
    MAX_COMPARABLE_ITEMS,
    PageOverlay,
    SingleDirectionDragAndDropStyled,
    WebsiteQueryBarContainer,
    getDragBox,
} from "./WebsiteQueryBar";
import type { StateOrName } from "@uirouter/react";
import { allTrackers } from "app/services/track/track";

interface WebsitePageQueryBarProps {
    filterRecentsBy?: StateOrName;
}

export const WebsitePageQueryBar: FC<WebsitePageQueryBarProps> = ({ filterRecentsBy }) => {
    const hideCompareButton = proNavigator.current()?.hideCompareButton ?? false;
    const [chosenItems, setChosenItems] = useState<IChosenItem[]>([]);
    const [similarSites, setSimilarSites] = useState<ISimilarSite[]>([]);
    const [isCompareButtonOpen, setIsCompareButtonOpen] = useState(false);
    const [activeItem, setActiveItem] = useState<string>(null);
    const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(false);
    const [compareSitesToBeAdded, setCompareSitesToBeAdded] = useState<IChosenItem[]>([]);
    const [listItemOrder, setListItemOrder] = useState<string[]>();
    const [shouldHideVersus, setShouldHideVersus] = useState(false);
    const { isLoading, recents } = useWebsitePageQueryBarData({
        filterRecentsBy,
        setChosenItems,
        setSimilarSites,
        setListItemOrder,
    });
    const dispatch = useDispatch();
    const { isCompareButtonOpen: shouldOpenCompare, isMainSiteDropdownOpen } = useSelector(
        (state) => state.QueryBarReducer,
    );

    useEffect(() => {
        if (shouldOpenCompare && !hideCompareButton) {
            setIsCompareButtonOpen(true);
            setIsOverlayOpen(true);
        } else if (isMainSiteDropdownOpen) {
            setIsOverlayOpen(true);
            setActiveItem(chosenItems[0]?.url);
        }
    }, [shouldOpenCompare, isMainSiteDropdownOpen]);

    const debouncedUpdateChosenItemsParams = useRef(
        debounce((isMainSite: boolean, siteList: IChosenItem[]) => {
            const pageFilter = transformSiteListToParam(siteList);
            const keyArr = siteList.map(({ domain, subdomain }) => getDomainWithSubdomain(domain, subdomain));
            const key = uniq(keyArr).join();
            const currentState = proNavigator.current();
            const params = {
                key,
                pageFilter,
                ...(isMainSite && currentState.resetOnMainSiteChange ? currentState.reset : {}),
            };
            proNavigator.updateParams(params);
        }, 800),
    );

    useEffect(() => {
        document.body.addEventListener("click", handleBodyClick, { capture: true });
        return () => {
            document.body.removeEventListener("click", handleBodyClick, { capture: true });
        };
    }, [chosenItems, compareSitesToBeAdded, isCompareButtonOpen]);

    const handleBodyClick = (e: MouseEvent) => {
        const targetElement = e.target as Element;
        const hasClickedOutsideOfQueryBar =
            !findParentByClass(targetElement, "AutocompleteWebsitePagesQueryBarContainer") &&
            !findParentByClass(targetElement, "AutocompleteWebsitePagesQueryBar") &&
            !findParentByClass(targetElement, "QueryBar-compare-button") &&
            !findParentByClass(targetElement, "loading-component");

        const isClickOnQueryBarItem =
            isCompareButtonOpen &&
            (findParentByClass(targetElement, "compare-site-to-be-added") ||
                findParentByClass(targetElement, "querybar-item"));

        if (hasClickedOutsideOfQueryBar && !isClickOnQueryBarItem) {
            if (isOverlayOpen) {
                dispatch({ type: TOGGLE_OFF_COMPARE_DROPDOWN });
            }
            if (isCompareButtonOpen) {
                updateParamsWithNewSites();
            }
            updateChosenItemsWithNewSites();
        }
    };

    const replaceKeyAndUpdateParams = (
        indexToReplace: number,
        replacementUrl: string,
        replacementSearchType?: WebsitePageSearchType,
    ) => {
        const { domain: newDomain, subdomain: newSubdomain, error } = getUrlComponents(replacementUrl);
        if (error) {
            return;
        }

        const urls = chosenItems.slice();
        urls.splice(indexToReplace, 1, {
            url: replacementUrl,
            searchType: replacementSearchType ?? (newSubdomain ? "subdomain" : "domain"),
        } as IChosenItem);
        const pageFilter = transformSiteListToParam(urls);

        const domains = chosenItems.map((chosenItem) =>
            getDomainWithSubdomain(chosenItem.domain, chosenItem.subdomain),
        );
        domains.splice(indexToReplace, 1, getDomainWithSubdomain(newDomain, newSubdomain));

        const currentState = proNavigator.current();
        const params = {
            pageFilter,
            key: uniq(domains).join(),
            ...(currentState.reset ?? {}),
        };
        proNavigator.updateParams(params);
    };

    const replaceItemAndUpdateState = (indexToReplace: number, replacementItem: ISite) => {
        setIsCompareButtonOpen(false);
        setActiveItem(null);
        setIsOverlayOpen(false);
        const items = chosenItems.map((chosenItem) => chosenItem);
        items.splice(
            indexToReplace,
            1,
            createChosenItem({
                url: (replacementItem as any).fullUrl ?? replacementItem.name,
                icon: replacementItem.image,
                image: replacementItem.image,
                favicon: replacementItem.image,
                color: replacementItem.color,
            }),
        );
        setChosenItems(items);
        setListItemOrder(items.map((item) => item.url));
    };

    const updateChosenItemsWithNewSites = (compareSites = compareSitesToBeAdded, addToCurrent = true) => {
        const updatedChosenItems = addToCurrent ? [...chosenItems, ...compareSites] : [...compareSites];
        setIsCompareButtonOpen(false);
        setActiveItem(null);
        setIsOverlayOpen(false);
        setCompareSitesToBeAdded([]);
        setChosenItems(updatedChosenItems);
        setListItemOrder(updatedChosenItems.map((item) => item.url));
    };

    const updateParamsWithNewSites = (newCompareList?: IChosenItem[], addToCurrent = true) => {
        const newItems: IChosenItemParams = newCompareList?.length
            ? newCompareList.map((item) => ({
                  url: item.url,
                  searchType: item.searchType,
              }))
            : compareSitesToBeAdded.map((item) => ({
                  url: item.url,
                  searchType: item.searchType,
              }));

        const urls = addToCurrent ? [...chosenItems, ...newItems] : newItems;
        const pageFilter = transformSiteListToParam(urls);
        const keyArr = urls.map(({ url }) => {
            const { domain, subdomain } = getUrlComponents(url);
            return getDomainWithSubdomain(domain, subdomain);
        });
        const key = uniq(keyArr).join();

        proNavigator.updateParams({ pageFilter, key });
    };

    const onPressEnter = (event, item: IWebsitePageResult, isMainItem: boolean) => {
        if (typeof item !== "object") {
            onCompareButtonAutocompleteCloseButtonClick();
        } else if (item) {
            const type = event.target.value?.length ? "query" : "similar";
            if (type === "query" && item?.selectedTextIndexes?.length) {
                item.name = item.name.slice(...item.selectedTextIndexes);
            }
            isMainItem ? onMainItemAutocompleteItemClick(item, type) : onCompareItemAutocompleteItemClick(item, type);
        } else {
            updateParamsWithNewSites();
            updateChosenItemsWithNewSites();
        }
    };

    const removeItem = (item: IChosenItem) => () => {
        let itemsWithoutRemovedItem;
        const isMainSite = item.url === chosenItems[0].url;
        if (isMainSite) {
            const currentComparedItemsArray = chosenItems.map((comparedItem) => comparedItem);
            currentComparedItemsArray.shift();
            currentComparedItemsArray[0].color = chosenItems[0].color;
            itemsWithoutRemovedItem = currentComparedItemsArray;
        } else {
            itemsWithoutRemovedItem = chosenItems.filter((comparedItem) => comparedItem.url !== item.url);
        }
        setChosenItems(itemsWithoutRemovedItem);
        setListItemOrder(itemsWithoutRemovedItem.map((item) => item.url));
        setActiveItem(null);
        queueMicrotask(() => debouncedUpdateChosenItemsParams.current(isMainSite, itemsWithoutRemovedItem));
    };

    const onCompareItemAutocompleteCloseButtonClick = () => {
        setActiveItem(null);
        if (!isCompareButtonOpen) {
            setIsOverlayOpen(false);
        }
    };

    const removeItemFromSitesToBeAddedList = (item: string) => {
        setCompareSitesToBeAdded((sites) => sites.filter((compareSite) => compareSite.url !== item));
    };

    const onMainItemAutocompleteClose = () => {
        setActiveItem(null);
        setIsOverlayOpen(false);
    };

    const onMainItemAutocompleteItemClick = (item: any, type: string) => {
        const comparedSites = item.compareItems?.map((site) => site.domain);
        const sites = !item.compareItems ? item.name : [item.mainSite.domain, comparedSites].join(",");
        allTrackers.dispatchTrackEvent("Search Bar", "click", `${type || ""}/Main Site/${sites || ""}`);
        if (item.compareItems) {
            const sitesToAdd = [];
            sitesToAdd.push(transformSite(item.mainSite, item.searchType));
            item.compareItems.forEach((compared) => {
                sitesToAdd.push(transformSite(compared, compared.searchType));
            });
            updateChosenItemsWithNewSites(sitesToAdd, false);
            updateParamsWithNewSites(sitesToAdd, false);
        } else {
            // update querybar items visually. (when replacing the main item for some reason the color property is
            // not immediately passed to the new main item. Therefore, we add it manually)
            item.color = chosenItems[0].color;

            // update params with new main site
            replaceKeyAndUpdateParams(0, item.fullUrl ?? item.name, item.searchType);
            replaceItemAndUpdateState(0, item);
        }
    };

    const onCompareItemAutocompleteItemClick = (item: IWebsitePageResult, type: string) => {
        allTrackers.dispatchTrackEvent("Search Bar", "click", `${type || ""}/Compare Site/${item.name || ""}`);
        const itemIndex = chosenItems.findIndex((chosenItem) => chosenItem.url === activeItem);
        if (itemIndex === -1) {
            onAddNewCompareItem(item, type);
        } else if (isCompareButtonOpen && compareSitesToBeAdded.length > 0) {
            const items: any[] = chosenItems.map((chosenItem) => chosenItem);
            items.splice(itemIndex, 1, item);
            setActiveItem(null);
            setChosenItems(items);
            setListItemOrder(items.map((item) => item.url));
        } else {
            replaceKeyAndUpdateParams(itemIndex, item.name, item.searchType);
            replaceItemAndUpdateState(itemIndex, item);
        }
    };

    const onCompareButtonAutocompleteCloseButtonClick = () => {
        if (compareSitesToBeAdded.length > 0) {
            updateChosenItemsWithNewSites();
            updateParamsWithNewSites();
            return;
        }
        setIsCompareButtonOpen(false);
        setActiveItem(null);
        setIsOverlayOpen(false);
        setCompareSitesToBeAdded([]);
    };

    const onAddNewCompareItem = (item: IWebsitePageResult, type: string) => {
        allTrackers.dispatchTrackEvent("Search Bar", "click", `${type || ""}/Compare Site/${item.name || ""}`);
        const similarSitesWithoutItem = similarSites.filter((x) => item.name !== x.Domain);
        setSimilarSites(similarSitesWithoutItem);

        const compareSitesAsChosenSites = compareSitesToBeAdded.slice();
        const newItemForAddList = createChosenItem({
            searchType: item.searchType,
            url: item.name,
            icon: item.icon,
            image: item.icon,
            favicon: item.icon,
            color: CHART_COLORS.compareMainColors[chosenItems.length + compareSitesAsChosenSites.length],
        });

        compareSitesAsChosenSites.push(newItemForAddList);
        // if we are at max # of total items, update url params.
        if (chosenItems.length + compareSitesAsChosenSites.length === 5) {
            updateChosenItemsWithNewSites(compareSitesAsChosenSites);
            updateParamsWithNewSites(compareSitesAsChosenSites);
            return;
        }

        setListItemOrder((state) => [...state, newItemForAddList.url]);
        setCompareSitesToBeAdded([...compareSitesToBeAdded, newItemForAddList]);
        setIsCompareButtonOpen(false);
        setTimeout(() => setIsCompareButtonOpen(true), 50);
    };

    const onCompareButtonClick = () => {
        SwTrack.all.trackEvent("Compare", "open", "Header");
        setIsCompareButtonOpen(true);
        setActiveItem(null);
        setIsOverlayOpen(true);
    };

    if (!chosenItems.length) {
        return;
    }
    const showBadge = !isCompareButtonOpen && chosenItems.length > 0;
    const numOfVisibleCompareItems = chosenItems.length - 1 + compareSitesToBeAdded.length;

    const mainItem = getMainItem({
        chosenItems,
        shouldShowBadge: showBadge,
        isLoading,
        activeItem,
        numOfVisibleCompareItems,
        recents,
        similarSites,
        removeItem,
        onMainItemAutocompleteItemClick,
        onMainItemAutocompleteClose,
        onPressEnter,
        onMainItemTextClick: (name: string) => () => {
            // while the user is choosing compare sites, changing the mainItem is not allowed.
            if (isCompareButtonOpen && compareSitesToBeAdded.length > 0) {
                setActiveItem(null);
                return;
            }
            setActiveItem(name);
            setIsOverlayOpen(true);
            setIsCompareButtonOpen(compareSitesToBeAdded.length > 0);
            allTrackers.dispatchTrackEvent("External Link", "click", `${name || ""}`);
        },
    });

    const getCompareItems = (): IDraggableItem[] => {
        const compareItems = getCompareList({
            chosenItems,
            shouldShowBadge: showBadge,
            isLoading,
            activeItem,
            compareSitesToBeAdded,
            recents,
            similarSites,
            removeItem,
            onCompareItemAutocompleteItemClick,
            onCompareItemAutocompleteCloseButtonClick,
            onPressEnter,
            removeItemFromSitesToBeAddedList,
            onClick: (name: string) => {
                setIsCompareButtonOpen(false);
                setActiveItem(name);
                setIsOverlayOpen(true);
            },
        });

        return compareItems.map((component: any) => ({
            id: component.key,
            component,
        }));
    };
    const listItems = [mainItem, ...getCompareItems()];

    const listItemsSorted = listItems.sort((a, b) => {
        if (listItemOrder.indexOf(a.id) > listItemOrder.indexOf(b.id)) {
            return 1;
        }
        if (listItemOrder.indexOf(a.id) < listItemOrder.indexOf(b.id)) {
            return -1;
        }
        return 0;
    });

    return (
        <>
            <WebsiteQueryBarContainer className="QueryBar u-flex-row u-flex-center AutocompleteWebsitePagesQueryBarContainer">
                {numOfVisibleCompareItems > 0 ? (
                    <SingleDirectionDragAndDropStyled
                        className="u-flex-row u-flex-center"
                        direction="horizontal"
                        items={listItemsSorted}
                        getItemComponent={(...args) => getDragBox(...args, shouldHideVersus)}
                        onDragStart={() => setShouldHideVersus(true)}
                        onDragEnd={(newColumns) => {
                            setShouldHideVersus(false);
                            changeQueryBarOrder(newColumns, chosenItems, listItemOrder, setListItemOrder);
                        }}
                    />
                ) : (
                    <>
                        {listItemsSorted[0].component}
                        {proNavigator.current()?.hideFavoriteButton ? null : (
                            <FavoriteButton
                                mainSite={chosenItems[0]?.url ?? ""}
                                isAccountReview={proNavigator.isAccountReview()}
                            />
                        )}
                    </>
                )}
                {!hideCompareButton ? (
                    <div style={{ position: "relative" }}>
                        {!isCompareButtonOpen &&
                        chosenItems.length + compareSitesToBeAdded.length < MAX_COMPARABLE_ITEMS
                            ? getButtonToDisplay(onCompareButtonClick, chosenItems.length > 1)
                            : null}
                        {isCompareButtonOpen ? (
                            isLoading ? (
                                <LoadingComponent
                                    loaderStyles={{ top: -16 }}
                                    placeholderText={translate("autocomplete.websitePagesCompare.placeholder.text")}
                                    headerText={translate(
                                        "autocomplete.websitePagesCompare.seperator.text.similarSites",
                                    )}
                                />
                            ) : (
                                <AutocompleteWebsitePagesCompareItem
                                    className="AutocompleteWebsitePagesQueryBar"
                                    similarSites={similarSites}
                                    autocompleteProps={{
                                        placeholder: translate(
                                            "querybar.websitePages.comparebutton.autocomplete.placeholder",
                                        ),
                                        top: -16,
                                    }}
                                    recentSearches={recents}
                                    onClick={onAddNewCompareItem}
                                    onCloseButtonClick={onCompareButtonAutocompleteCloseButtonClick}
                                    excludes={[...chosenItems, ...compareSitesToBeAdded]}
                                    overrideEnterFunc={(event, item) => onPressEnter(event, item, false)}
                                />
                            )
                        ) : null}
                    </div>
                ) : null}
            </WebsiteQueryBarContainer>
            <PageOverlay isOpen={isOverlayOpen} />
        </>
    );
};
